import { useState, useCallback, useEffect } from "react";
import { useTranslate } from "@refinedev/core";
import type { Stripe } from "@stripe/stripe-js";

let stripePromise: Promise<Stripe | null> | undefined;

const getStripe = async () => {
  if (stripePromise === undefined) {
    const { loadStripe } = await import("@stripe/stripe-js");
    const publishableKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;
    stripePromise = loadStripe(publishableKey);
  }
  return stripePromise;
};

interface UseStripePaymentOptions {
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

export function useStripePayment({
  onSuccess,
  onError,
}: UseStripePaymentOptions = {}) {
  const t = useTranslate();
  const [isProcessing, setIsProcessing] = useState(false);
  const [stripe, setStripe] = useState<Stripe | null>(null);

  useEffect(() => {
    getStripe().then((s) => {
      setStripe(s);
    });
  }, []);

  const confirmPayment = useCallback(
    async (clientSecret: string) => {
      if (!stripe) {
        const error = t("stripePayment.errors.stripeNotLoaded");
        onError?.(error);
        return { success: false, error };
      }

      setIsProcessing(true);

      try {
        const result = await stripe.confirmPayment({
          clientSecret,
          confirmParams: {
            return_url: window.location.href,
          },
          redirect: "if_required",
        });

        const { error, paymentIntent } = result;

        if (error) {
          const errorMessage =
            error.message ?? t("stripePayment.errors.paymentFailed");
          onError?.(errorMessage);
          return { success: false, error: errorMessage };
        }

        if (
          paymentIntent?.status === "succeeded" ||
          paymentIntent?.status === "processing"
        ) {
          onSuccess?.();
          return { success: true };
        }

        // Handle requires_action - user might need to complete 3D Secure
        if (paymentIntent?.status === "requires_action") {
          // Stripe should handle this with redirect: "if_required"
          // but if we get here, the action wasn't handled
          const authError = t("stripePayment.errors.authenticationRequired");
          onError?.(authError);
          return { success: false, error: authError };
        }

        const statusError = t("stripePayment.errors.unexpectedStatus", {
          status: paymentIntent?.status,
        });
        onError?.(statusError);
        return { success: false, error: statusError };
      } finally {
        setIsProcessing(false);
      }
    },
    [stripe, onSuccess, onError, t]
  );

  return {
    stripe,
    isReady: !!stripe,
    isProcessing,
    confirmPayment,
  };
}
